/**
 * AudioRecorderService - Professional-grade audio recording service
 * Based on Android: services/audio/AudioRecorderService.kt
 *
 * Features:
 * - Browser-based audio recording using MediaRecorder API
 * - Real-time audio level monitoring
 * - Recording pause/resume
 * - Multiple format support (WebM, WAV)
 * - Recording metadata management
 */

import {
  RecordingFormat,
  RecordingQuality,
  RecordingMetadata,
  RecordingError,
  RecordingErrors,
  formatDuration,
  formatFileSize,
  getBestMimeType,
} from './audio-types'
import { createClient } from '@/lib/supabase/client'

const LEVEL_UPDATE_INTERVAL_MS = 50

export interface AudioRecorderState {
  isRecording: boolean
  isPaused: boolean
  hasPermission: boolean
  recordingDuration: number
  recordingFile: RecordingMetadata | null
  audioLevel: number
  peakLevel: number
  error: RecordingError | null
  recordingFormat: RecordingFormat
  recordingQuality: RecordingQuality
  recordings: RecordingMetadata[]
}

class AudioRecorderService {
  private mediaRecorder: MediaRecorder | null = null
  private audioContext: AudioContext | null = null
  private analyser: AnalyserNode | null = null
  private mediaStream: MediaStream | null = null
  private chunks: Blob[] = []
  private recordingStartTime = 0
  private durationInterval: number | null = null
  private levelInterval: number | null = null

  private state: AudioRecorderState = {
    isRecording: false,
    isPaused: false,
    hasPermission: false,
    recordingDuration: 0,
    recordingFile: null,
    audioLevel: 0,
    peakLevel: 0,
    error: null,
    recordingFormat: RecordingFormat.WEBM,
    recordingQuality: RecordingQuality.HIGH,
    recordings: [],
  }

  private listeners: Set<(state: AudioRecorderState) => void> = new Set()

  constructor() {
    this.loadRecordings()
  }

  // ============================================================================
  // State Management
  // ============================================================================

  getState(): AudioRecorderState {
    return { ...this.state }
  }

  subscribe(listener: (state: AudioRecorderState) => void): () => void {
    this.listeners.add(listener)
    return () => this.listeners.delete(listener)
  }

  private setState(updates: Partial<AudioRecorderState>): void {
    this.state = { ...this.state, ...updates }
    this.listeners.forEach((listener) => listener(this.getState()))
  }

  // ============================================================================
  // Permission Management
  // ============================================================================

  async checkPermission(): Promise<boolean> {
    try {
      // Check if MediaRecorder is supported
      if (typeof MediaRecorder === 'undefined') {
        console.warn('[AudioRecorder] MediaRecorder not supported')
        this.setState({ hasPermission: false })
        return false
      }

      // Check permission state if available
      if (navigator.permissions) {
        try {
          const result = await navigator.permissions.query({ name: 'microphone' as PermissionName })
          const hasPermission = result.state === 'granted'
          this.setState({ hasPermission })
          return hasPermission
        } catch {
          // Some browsers don't support microphone permission query
        }
      }

      // Try to get media stream to check permission
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      stream.getTracks().forEach((track) => track.stop())
      this.setState({ hasPermission: true })
      return true
    } catch (error) {
      console.warn('[AudioRecorder] Permission check failed:', error)
      this.setState({ hasPermission: false })
      return false
    }
  }

  async requestPermission(): Promise<boolean> {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      stream.getTracks().forEach((track) => track.stop())
      this.setState({ hasPermission: true })
      return true
    } catch (error) {
      console.error('[AudioRecorder] Permission request failed:', error)
      this.setState({ hasPermission: false, error: RecordingErrors.permissionDenied() })
      return false
    }
  }

  // ============================================================================
  // Recording Configuration
  // ============================================================================

  setRecordingFormat(format: RecordingFormat): void {
    if (!this.state.isRecording) {
      this.setState({ recordingFormat: format })
      console.log(`[AudioRecorder] Recording format set to: ${format}`)
    }
  }

  setRecordingQuality(quality: RecordingQuality): void {
    if (!this.state.isRecording) {
      this.setState({ recordingQuality: quality })
      console.log(`[AudioRecorder] Recording quality set to: ${quality}`)
    }
  }

  // ============================================================================
  // Recording Control
  // ============================================================================

  async startRecording(): Promise<RecordingMetadata | null> {
    // Check browser support
    if (typeof MediaRecorder === 'undefined') {
      this.setState({ error: RecordingErrors.notSupported() })
      return null
    }

    // Check permission
    const hasPermission = await this.checkPermission()
    if (!hasPermission) {
      const granted = await this.requestPermission()
      if (!granted) {
        return null
      }
    }

    if (this.state.isRecording) {
      console.warn('[AudioRecorder] Recording already in progress')
      return this.state.recordingFile
    }

    this.setState({ error: null })

    try {
      // Get media stream
      this.mediaStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      })

      // Set up audio analysis for level monitoring
      this.setupAudioAnalyser(this.mediaStream)

      // Get best supported MIME type
      const mimeType = getBestMimeType()
      if (!mimeType) {
        throw new Error('No supported audio MIME type found')
      }

      // Create MediaRecorder
      this.mediaRecorder = new MediaRecorder(this.mediaStream, {
        mimeType,
        audioBitsPerSecond: 128000,
      })

      this.chunks = []

      this.mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          this.chunks.push(event.data)
        }
      }

      this.mediaRecorder.onerror = (event) => {
        console.error('[AudioRecorder] MediaRecorder error:', event)
        this.setState({ error: RecordingErrors.recordingFailed('MediaRecorder error') })
      }

      // Start recording
      this.mediaRecorder.start(100) // Collect data every 100ms

      this.recordingStartTime = Date.now()

      const metadata: RecordingMetadata = {
        id: crypto.randomUUID(),
        filename: `recording_${new Date().toISOString().replace(/[:.]/g, '-')}.${this.state.recordingFormat}`,
        durationMs: 0,
        fileSize: 0,
        format: this.state.recordingFormat,
        quality: this.state.recordingQuality,
        createdAt: Date.now(),
        tags: [],
      }

      this.setState({
        isRecording: true,
        isPaused: false,
        recordingFile: metadata,
        recordingDuration: 0,
        peakLevel: 0,
      })

      this.startDurationUpdates()
      this.startLevelUpdates()

      console.log('[AudioRecorder] Recording started')
      return metadata
    } catch (error) {
      console.error('[AudioRecorder] Failed to start recording:', error)
      this.setState({
        error: RecordingErrors.recordingFailed(
          error instanceof Error ? error.message : 'Unknown error'
        ),
      })
      this.cleanup()
      return null
    }
  }

  pauseRecording(): void {
    if (!this.state.isRecording || this.state.isPaused) return

    if (this.mediaRecorder?.state === 'recording') {
      this.mediaRecorder.pause()
    }

    this.setState({ isPaused: true })
    this.stopDurationUpdates()
    this.stopLevelUpdates()

    console.log(`[AudioRecorder] Recording paused at ${formatDuration(this.state.recordingDuration)}`)
  }

  resumeRecording(): void {
    if (!this.state.isRecording || !this.state.isPaused) return

    if (this.mediaRecorder?.state === 'paused') {
      this.mediaRecorder.resume()
    }

    this.setState({ isPaused: false })
    this.startDurationUpdates()
    this.startLevelUpdates()

    console.log('[AudioRecorder] Recording resumed')
  }

  async stopRecording(): Promise<RecordingMetadata | null> {
    if (!this.state.isRecording) return null

    console.log('[AudioRecorder] Stopping recording...')

    return new Promise((resolve) => {
      if (!this.mediaRecorder) {
        resolve(null)
        return
      }

      this.mediaRecorder.onstop = async () => {
        try {
          const blob = new Blob(this.chunks, { type: this.mediaRecorder?.mimeType || 'audio/webm' })
          const duration = this.state.recordingDuration

          const metadata: RecordingMetadata = {
            id: this.state.recordingFile?.id || crypto.randomUUID(),
            filename: this.state.recordingFile?.filename || `recording_${Date.now()}.webm`,
            durationMs: duration,
            fileSize: blob.size,
            format: this.state.recordingFormat,
            quality: this.state.recordingQuality,
            createdAt: this.state.recordingFile?.createdAt || Date.now(),
            tags: [],
            blob,
          }

          // Add to recordings list
          const recordings = [metadata, ...this.state.recordings]
          this.setState({ recordings })
          this.saveRecordings()

          this.cleanup()
          this.setState({
            isRecording: false,
            isPaused: false,
            recordingDuration: 0,
            recordingFile: null,
          })

          this.stopDurationUpdates()
          this.stopLevelUpdates()

          console.log(
            `[AudioRecorder] Recording saved: ${metadata.filename} (${formatDuration(duration)}, ${formatFileSize(blob.size)})`
          )
          resolve(metadata)
        } catch (error) {
          console.error('[AudioRecorder] Error stopping recording:', error)
          this.setState({
            error: RecordingErrors.recordingFailed('Failed to stop recording'),
          })
          this.cleanup()
          resolve(null)
        }
      }

      this.mediaRecorder.stop()
    })
  }

  async uploadRecording(recording: RecordingMetadata, userId: string): Promise<boolean> {
    if (!recording.blob) return false

    console.log('[AudioRecorder] Uploading recording...', recording.id)
    const supabase = createClient()

    try {
      // 1. Upload to Storage
      const filePath = `${userId}/${recording.id}.${recording.format}`
      const { error: uploadError } = await supabase.storage
        .from('audio-recordings')
        .upload(filePath, recording.blob, {
          contentType: recording.blob.type,
          upsert: true
        })

      if (uploadError) throw uploadError

      const { data: { publicUrl } } = supabase.storage
        .from('audio-recordings')
        .getPublicUrl(filePath)

      // 2. Create generic Entry
      const { data: entry, error: entryError } = await supabase
        .from('entries')
        .insert({
          user_id: userId,
          type: 'audio',
          title: `Voice Recording ${new Date(recording.createdAt).toLocaleString()}`,
          content: recording.transcription || 'Audio recording',
          url: publicUrl,
          platform: 'linkbox_voice',
          metadata: {
            duration: recording.durationMs,
            format: recording.format,
            size: recording.fileSize
          }
        })
        .select()
        .single()

      if (entryError) throw entryError

      // 3. Create Audio Entry
      const { error: audioError } = await supabase
        .from('audio_entries')
        .insert({
          id: crypto.randomUUID(),
          user_id: userId,
          entry_id: entry.id,
          file_path: filePath,
          duration: recording.durationMs,
          format: recording.format,
          size_bytes: recording.fileSize,
          transcription: recording.transcription
        })

      if (audioError) throw audioError

      console.log('[AudioRecorder] Upload successful')
      return true
    } catch (error) {
      console.error('[AudioRecorder] Upload failed:', error)
      return false
    }
  }

  cancelRecording(): void {
    console.log('[AudioRecorder] Cancelling recording...')

    this.cleanup()
    this.chunks = []

    this.setState({
      isRecording: false,
      isPaused: false,
      recordingDuration: 0,
      recordingFile: null,
    })

    this.stopDurationUpdates()
    this.stopLevelUpdates()

    console.log('[AudioRecorder] Recording cancelled')
  }

  private cleanup(): void {
    if (this.mediaRecorder) {
      if (this.mediaRecorder.state !== 'inactive') {
        try {
          this.mediaRecorder.stop()
        } catch {
          // Ignore
        }
      }
      this.mediaRecorder = null
    }

    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach((track) => track.stop())
      this.mediaStream = null
    }

    if (this.audioContext) {
      this.audioContext.close()
      this.audioContext = null
    }

    this.analyser = null
  }

  // ============================================================================
  // Audio Analysis
  // ============================================================================

  private setupAudioAnalyser(stream: MediaStream): void {
    try {
      this.audioContext = new AudioContext()
      this.analyser = this.audioContext.createAnalyser()
      this.analyser.fftSize = 256

      const source = this.audioContext.createMediaStreamSource(stream)
      source.connect(this.analyser)
    } catch (error) {
      console.warn('[AudioRecorder] Failed to set up audio analyser:', error)
    }
  }

  private calculateAudioLevel(): number {
    if (!this.analyser) return 0

    const dataArray = new Uint8Array(this.analyser.frequencyBinCount)
    this.analyser.getByteFrequencyData(dataArray)

    // Calculate RMS
    let sum = 0
    for (let i = 0; i < dataArray.length; i++) {
      sum += dataArray[i] * dataArray[i]
    }
    const rms = Math.sqrt(sum / dataArray.length)

    // Normalize to 0-1
    return Math.min(1, rms / 128)
  }

  // ============================================================================
  // Duration Updates
  // ============================================================================

  private startDurationUpdates(): void {
    this.durationInterval = window.setInterval(() => {
      if (this.state.isRecording && !this.state.isPaused) {
        const duration = Date.now() - this.recordingStartTime
        this.setState({ recordingDuration: duration })
      }
    }, 100)
  }

  private stopDurationUpdates(): void {
    if (this.durationInterval) {
      clearInterval(this.durationInterval)
      this.durationInterval = null
    }
  }

  // ============================================================================
  // Level Updates
  // ============================================================================

  private startLevelUpdates(): void {
    this.levelInterval = window.setInterval(() => {
      if (this.state.isRecording && !this.state.isPaused) {
        const level = this.calculateAudioLevel()
        this.setState({
          audioLevel: level,
          peakLevel: Math.max(this.state.peakLevel, level),
        })
      }
    }, LEVEL_UPDATE_INTERVAL_MS)
  }

  private stopLevelUpdates(): void {
    if (this.levelInterval) {
      clearInterval(this.levelInterval)
      this.levelInterval = null
    }
    this.setState({ audioLevel: 0 })
  }

  // ============================================================================
  // Recording Management
  // ============================================================================

  deleteRecording(id: string): boolean {
    const recordings = this.state.recordings.filter((r) => r.id !== id)
    if (recordings.length !== this.state.recordings.length) {
      this.setState({ recordings })
      this.saveRecordings()
      console.log(`[AudioRecorder] Deleted recording: ${id}`)
      return true
    }
    return false
  }

  getRecording(id: string): RecordingMetadata | undefined {
    return this.state.recordings.find((r) => r.id === id)
  }

  // ============================================================================
  // Persistence
  // ============================================================================

  private saveRecordings(): void {
    try {
      // Save metadata (without blobs) to localStorage
      const metadataOnly = this.state.recordings.map(({ blob, ...rest }) => rest)
      localStorage.setItem('linkbox_recordings', JSON.stringify(metadataOnly))
    } catch (error) {
      console.warn('[AudioRecorder] Failed to save recordings:', error)
    }
  }

  private loadRecordings(): void {
    if (typeof window === 'undefined') return;
    try {
      const saved = localStorage.getItem('linkbox_recordings')
      if (saved) {
        const recordings = JSON.parse(saved) as RecordingMetadata[]
        this.setState({ recordings })
        console.log(`[AudioRecorder] Loaded ${recordings.length} recordings`)
      }
    } catch (error) {
      console.warn('[AudioRecorder] Failed to load recordings:', error)
    }
  }

  // ============================================================================
  // Utility
  // ============================================================================

  isSupported(): boolean {
    return typeof MediaRecorder !== 'undefined' && navigator.mediaDevices !== undefined
  }

  release(): void {
    this.cancelRecording()
    this.stopDurationUpdates()
    this.stopLevelUpdates()
  }
}

// Singleton instance
let instance: AudioRecorderService | null = null

export function getAudioRecorderService(): AudioRecorderService {
  if (!instance) {
    instance = new AudioRecorderService()
  }
  return instance
}

export default AudioRecorderService
